// src/components/PushNotificationWelcomeModal.tsx

import { useState } from "react";
import { ZodiyakBubble } from "./ZodiyakBubble";
import { ZodiacSign } from "@/utils/zodiac";

interface PushNotificationWelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEnablePush: () => Promise<void>;
  onDismiss: () => Promise<void>;
  zodiacSign?: ZodiacSign | null;
}

export default function PushNotificationWelcomeModal({
  isOpen,
  onClose,
  onEnablePush,
  onDismiss,
  zodiacSign,
}: PushNotificationWelcomeModalProps) {
  const [loading, setLoading] = useState(false);
  const [iosStep, setIosStep] = useState<'install' | 'enable'>('install');
  const [enableHov,      setEnableHov]      = useState(false);
  const [enablePress,    setEnablePress]    = useState(false);
  const [skipHov,        setSkipHov]        = useState(false);
  const [skipPress,      setSkipPress]      = useState(false);
  const [ios1PrimHov,    setIos1PrimHov]    = useState(false);
  const [ios1PrimPress,  setIos1PrimPress]  = useState(false);
  const [ios1SkipHov,    setIos1SkipHov]    = useState(false);
  const [ios1SkipPress,  setIos1SkipPress]  = useState(false);

  if (!isOpen) return null;

  const displaySign = zodiacSign || "Aquarius";
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = !isIOS && /Android/.test(navigator.userAgent);

  const handleEnable = async () => {
    setLoading(true);
    try {
      await onEnablePush();
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const handleSkip = async () => {
    try {
      await onDismiss();
    } finally {
      onClose();
    }
  };

  // Shared styles
  const primaryBtnBase: React.CSSProperties = {
    color: "#FFFFFF",
    border: "3px solid #1A1A1A",
    borderRadius: 50,
    boxShadow: "4px 4px 0px #1A1A1A",
    padding: "12px 24px",
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 17,
    fontWeight: 600,
    flex: 1,
    cursor: loading ? "default" : "pointer",
    opacity: loading ? 0.65 : 1,
    boxSizing: "border-box",
    transition: "all 0.1s ease",
  };

  const secondaryBtnBase: React.CSSProperties = {
    color: "#1A1A1A",
    border: "3px solid #1A1A1A",
    borderRadius: 50,
    boxShadow: "4px 4px 0px #1A1A1A",
    padding: "12px 24px",
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 17,
    fontWeight: 600,
    cursor: loading ? "default" : "pointer",
    opacity: loading ? 0.65 : 1,
    boxSizing: "border-box",
    transition: "all 0.1s ease",
  };

  const instructionBox: React.CSSProperties = {
    background: '#fff9f7',
    border: '1px solid #e8c9c0',
    borderRadius: 10,
    padding: '10px 14px',
    marginBottom: 14,
    fontSize: 13,
    color: '#555',
    lineHeight: 1.5,
    textAlign: 'center',
  };

  // Shared enable screen — heading, body, coral subtext, Enable + Skip buttons.
  // No instruction box: iOS step 2 uses this directly; non-iOS callers append
  // their own instruction box before it.
  const EnableScreen = () => (
    <>
      <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 20, fontWeight: 700, color: "#1A1A1A", marginTop: 0, marginBottom: 8 }}>
        Secret Crush &amp; Match Notifications
      </h2>
      <p style={{ fontSize: 15, color: '#333', textAlign: 'center', marginBottom: 12 }}>
        Find out when someone has a secret crush on you or when you match.
      </p>
      <p style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 15, fontWeight: 600, color: "#E05A3A", marginTop: 0, marginBottom: 24 }}>
        No ads. No spam. Ever.
      </p>
      <div style={{ display: "flex", gap: 12 }}>
        <button
          onClick={handleEnable}
          disabled={loading}
          onMouseEnter={() => !loading && setEnableHov(true)}
          onMouseLeave={() => { setEnableHov(false); setEnablePress(false); }}
          onMouseDown={() => !loading && setEnablePress(true)}
          onMouseUp={() => setEnablePress(false)}
          style={{ ...primaryBtnBase, background: (!loading && enablePress) ? "#B34428" : (!loading && enableHov) ? "#C94E30" : "#E05A3A", transform: (!loading && enablePress) ? "scale(0.97)" : "scale(1)" }}
        >
          {loading ? "Enabling..." : "Enable Notifications"}
        </button>
        <button
          onClick={handleSkip}
          disabled={loading}
          onMouseEnter={() => !loading && setSkipHov(true)}
          onMouseLeave={() => { setSkipHov(false); setSkipPress(false); }}
          onMouseDown={() => !loading && setSkipPress(true)}
          onMouseUp={() => setSkipPress(false)}
          style={{ ...secondaryBtnBase, background: (!loading && skipPress) ? "rgba(0,0,0,0.12)" : (!loading && skipHov) ? "rgba(0,0,0,0.06)" : "#FFFFFF", transform: (!loading && skipPress) ? "scale(0.97)" : "scale(1)" }}
        >
          Skip
        </button>
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="max-w-2xl w-full flex flex-col items-center gap-6 p-4">

        {/* Zodiyak Character with Bubble */}
        <div className="flex items-start gap-3">
          <img
            src={`/SVG/zodiyak-${displaySign.toLowerCase()}.svg`}
            alt="Your Zodiyak guide"
            className="w-40 h-40 object-contain"
          />
          <ZodiyakBubble>
            <p className="text-2xl p-1 font-semibold text-gray-800">
              This Could Be the One...
            </p>
          </ZodiyakBubble>
        </div>

        {/* Info Modal Box — brand white card */}
        <div style={{ background: "#FFFFFF", border: "3px solid #1A1A1A", borderRadius: 20, boxShadow: "6px 6px 0px #1A1A1A", padding: "28px 24px", maxWidth: 380, width: "100%", boxSizing: "border-box" }}>

          {/* ── iOS Step 1: install prompt ── */}
          {isIOS && iosStep === 'install' && (
            <>
              <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1a1a', textAlign: 'center', marginBottom: 12 }}>
                One Quick Step First
              </h2>
              <p style={{ fontSize: 15, color: '#333', textAlign: 'center', marginBottom: 16 }}>
                To receive push notifications on iPhone, add Crushhh to your Home Screen first.
              </p>
              <div style={{ background: '#fff9f7', border: '1px solid #e8c9c0', borderRadius: 10, padding: '12px 14px', marginBottom: 20, fontSize: 14, color: '#555', lineHeight: 1.7, textAlign: 'center' }}>
                Tap <strong>( ⬆ Share )</strong><br />
                then → <strong>( Add to Home Screen )</strong>
              </div>
              <button
                onClick={() => setIosStep('enable')}
                onMouseEnter={() => setIos1PrimHov(true)}
                onMouseLeave={() => { setIos1PrimHov(false); setIos1PrimPress(false); }}
                onMouseDown={() => setIos1PrimPress(true)}
                onMouseUp={() => setIos1PrimPress(false)}
                style={{ width: '100%', padding: '12px 24px', borderRadius: 50, border: '3px solid #1A1A1A', background: ios1PrimPress ? '#B34428' : ios1PrimHov ? '#C94E30' : '#E05A3A', color: '#FFFFFF', fontFamily: "'Fredoka', sans-serif", fontSize: 17, fontWeight: 600, boxShadow: '4px 4px 0px #1A1A1A', cursor: 'pointer', boxSizing: 'border-box', marginBottom: 10, transform: ios1PrimPress ? 'scale(0.97)' : 'scale(1)', transition: 'all 0.1s ease' }}
              >
                I've Added It →
              </button>
              <button
                onClick={handleSkip}
                onMouseEnter={() => setIos1SkipHov(true)}
                onMouseLeave={() => { setIos1SkipHov(false); setIos1SkipPress(false); }}
                onMouseDown={() => setIos1SkipPress(true)}
                onMouseUp={() => setIos1SkipPress(false)}
                style={{ width: '100%', padding: '12px 24px', borderRadius: 50, border: '3px solid #1A1A1A', background: ios1SkipPress ? 'rgba(0,0,0,0.12)' : ios1SkipHov ? 'rgba(0,0,0,0.06)' : '#FFFFFF', color: '#1A1A1A', fontFamily: "'Fredoka', sans-serif", fontSize: 17, fontWeight: 600, boxShadow: '4px 4px 0px #1A1A1A', cursor: 'pointer', boxSizing: 'border-box', transform: ios1SkipPress ? 'scale(0.97)' : 'scale(1)', transition: 'all 0.1s ease' }}
              >
                Skip
              </button>
            </>
          )}

          {/* ── iOS Step 2: enable screen, no install box ── */}
          {isIOS && iosStep === 'enable' && <EnableScreen />}

          {/* ── Android: enable screen + Android instruction ── */}
          {!isIOS && isAndroid && (
            <>
              <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 20, fontWeight: 700, color: "#1A1A1A", marginTop: 0, marginBottom: 8 }}>
                Secret Crush &amp; Match Notifications
              </h2>
              <p style={{ fontSize: 15, color: '#333', textAlign: 'center', marginBottom: 12 }}>
                Find out when someone has a secret crush on you or when you match.
              </p>
              <p style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 15, fontWeight: 600, color: "#E05A3A", marginTop: 0, marginBottom: 14 }}>
                No ads. No spam. Ever.
              </p>
              <div style={instructionBox}>
                Add Crushhh to your Home Screen for instant push notifications.<br />
                Tap <strong>( ⋮ )</strong> then → <strong>( Add to Home Screen )</strong>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={handleEnable}
                  disabled={loading}
                  onMouseEnter={() => !loading && setEnableHov(true)}
                  onMouseLeave={() => { setEnableHov(false); setEnablePress(false); }}
                  onMouseDown={() => !loading && setEnablePress(true)}
                  onMouseUp={() => setEnablePress(false)}
                  style={{ ...primaryBtnBase, background: (!loading && enablePress) ? "#B34428" : (!loading && enableHov) ? "#C94E30" : "#E05A3A", transform: (!loading && enablePress) ? "scale(0.97)" : "scale(1)" }}
                >
                  {loading ? "Enabling..." : "Enable Notifications"}
                </button>
                <button
                  onClick={handleSkip}
                  disabled={loading}
                  onMouseEnter={() => !loading && setSkipHov(true)}
                  onMouseLeave={() => { setSkipHov(false); setSkipPress(false); }}
                  onMouseDown={() => !loading && setSkipPress(true)}
                  onMouseUp={() => setSkipPress(false)}
                  style={{ ...secondaryBtnBase, background: (!loading && skipPress) ? "rgba(0,0,0,0.12)" : (!loading && skipHov) ? "rgba(0,0,0,0.06)" : "#FFFFFF", transform: (!loading && skipPress) ? "scale(0.97)" : "scale(1)" }}
                >
                  Skip
                </button>
              </div>
            </>
          )}

          {/* ── Desktop / undetected: enable screen + combined instruction ── */}
          {!isIOS && !isAndroid && (
            <>
              <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 20, fontWeight: 700, color: "#1A1A1A", marginTop: 0, marginBottom: 8 }}>
                Secret Crush &amp; Match Notifications
              </h2>
              <p style={{ fontSize: 15, color: '#333', textAlign: 'center', marginBottom: 12 }}>
                Find out when someone has a secret crush on you or when you match.
              </p>
              <p style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 15, fontWeight: 600, color: "#E05A3A", marginTop: 0, marginBottom: 14 }}>
                No ads. No spam. Ever.
              </p>
              <div style={instructionBox}>
                Add Crushhh to your Home Screen for instant push notifications.<br /><br />
                On iPhone tap <strong>( ⬆ Share )</strong> → <strong>( Add to Home Screen )</strong>.<br /><br />
                On Android tap <strong>( ⋮ )</strong> → <strong>( Add to Home Screen )</strong>.
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={handleEnable}
                  disabled={loading}
                  onMouseEnter={() => !loading && setEnableHov(true)}
                  onMouseLeave={() => { setEnableHov(false); setEnablePress(false); }}
                  onMouseDown={() => !loading && setEnablePress(true)}
                  onMouseUp={() => setEnablePress(false)}
                  style={{ ...primaryBtnBase, background: (!loading && enablePress) ? "#B34428" : (!loading && enableHov) ? "#C94E30" : "#E05A3A", transform: (!loading && enablePress) ? "scale(0.97)" : "scale(1)" }}
                >
                  {loading ? "Enabling..." : "Enable Notifications"}
                </button>
                <button
                  onClick={handleSkip}
                  disabled={loading}
                  onMouseEnter={() => !loading && setSkipHov(true)}
                  onMouseLeave={() => { setSkipHov(false); setSkipPress(false); }}
                  onMouseDown={() => !loading && setSkipPress(true)}
                  onMouseUp={() => setSkipPress(false)}
                  style={{ ...secondaryBtnBase, background: (!loading && skipPress) ? "rgba(0,0,0,0.12)" : (!loading && skipHov) ? "rgba(0,0,0,0.06)" : "#FFFFFF", transform: (!loading && skipPress) ? "scale(0.97)" : "scale(1)" }}
                >
                  Skip
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
